import { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { getDocuments, updateDocumentStatus, uploadDocument, getCompanies } from '../services/api';
import { toast } from 'sonner';
import { motion, AnimatePresence } from 'framer-motion';
import {
  DndContext,
  DragOverlay,
  closestCorners,
  KeyboardSensor,
  PointerSensor,
  useSensor,
  useSensors,
} from '@dnd-kit/core';
import {
  SortableContext,
  sortableKeyboardCoordinates,
  verticalListSortingStrategy,
  useSortable,
} from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import {
  FileText, Upload, ChevronDown,
  Building2, Search, X, RefreshCw, Flame, Zap, CheckCircle, Calendar
} from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger } from '../components/ui/dropdown-menu';
import Sidebar from '../components/layout/Sidebar';
import DocumentDrawer from '../components/DocumentDrawer';

const COLUMNS = [
  { id: 'open', title: 'Neu & Dringend', color: 'orange', bgClass: 'bg-gradient-to-r from-orange-500/20 to-red-500/20', borderClass: 'border-orange-500/30', textClass: 'text-orange-500', icon: Flame },
  { id: 'progress', title: 'In Bearbeitung', color: 'blue', bgClass: 'bg-gradient-to-r from-blue-500/20 to-cyan-500/20', borderClass: 'border-blue-500/30', textClass: 'text-blue-500', icon: Zap },
  { id: 'done', title: 'Erledigt', color: 'emerald', bgClass: 'bg-gradient-to-r from-emerald-500/20 to-teal-500/20', borderClass: 'border-emerald-500/30', textClass: 'text-emerald-500', icon: CheckCircle },
];

const statusMapping = { 'urgent': 'open', 'pipeline': 'open', 'progress': 'progress', 'done': 'done' };

const SortableCard = ({ doc, onClick }) => {
  const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({ id: doc.id });
  const style = { transform: CSS.Transform.toString(transform), transition, opacity: isDragging ? 0.5 : 1 };

  const formatCurrency = (amount) => new Intl.NumberFormat('de-DE', { style: 'currency', currency: 'EUR' }).format(amount || 0);
  const isOverdue = () => doc.due_date && new Date(doc.due_date) < new Date();

  return (
    <div ref={setNodeRef} style={style} {...attributes} {...listeners} onClick={onClick}
      className={`bg-white/80 dark:bg-white/10 backdrop-blur-sm px-3 py-2.5 rounded-xl cursor-grab active:cursor-grabbing mb-2 hover:scale-[1.01] hover:shadow-md transition-all border ${
        isOverdue() ? 'border-red-500/50 shadow-red-500/10' : 'border-white/20 dark:border-white/10'
      }`}
      data-testid={`document-card-${doc.id}`}>
      <div className="flex items-center justify-between gap-2">
        <div className="flex items-center gap-2 min-w-0 flex-1">
          <div className={`w-8 h-8 rounded-lg flex items-center justify-center flex-shrink-0 ${
            doc.doc_type === 'Mahnung' ? 'bg-gradient-to-br from-red-500 to-rose-600 text-white' :
            doc.doc_type === 'Rechnung' ? 'bg-gradient-to-br from-blue-500 to-indigo-600 text-white' : 
            'bg-gradient-to-br from-gray-400 to-gray-500 text-white'
          }`}>
            <FileText className="w-4 h-4" strokeWidth={1.5} />
          </div>
          <div className="min-w-0 flex-1">
            <p className="font-medium text-sm text-foreground truncate">{doc.sender_name || doc.original_name || 'Unbekannt'}</p>
            <div className="flex items-center gap-2 text-xs text-muted-foreground">
              <span>{doc.doc_type || 'Dokument'}</span>
              {doc.due_date && (
                <span className={`flex items-center gap-0.5 ${isOverdue() ? 'text-red-500 font-medium' : ''}`}>
                  <Calendar className="w-3 h-3" strokeWidth={1.5} />
                  {new Date(doc.due_date).toLocaleDateString('de-DE', { day: '2-digit', month: '2-digit' })}
                </span>
              )}
            </div>
          </div>
        </div>
        <span className="font-semibold text-foreground tabular-nums text-sm whitespace-nowrap">{formatCurrency(doc.amount)}</span>
      </div>
    </div>
  );
};

const Column = ({ column, documents, onCardClick }) => {
  const { setNodeRef } = useSortable({ id: column.id, data: { type: 'column' } });
  const Icon = column.icon;
  const totalAmount = documents.reduce((sum, d) => sum + (d.amount || 0), 0);
  const formatCurrency = (amount) => new Intl.NumberFormat('de-DE', { style: 'currency', currency: 'EUR' }).format(amount || 0);

  return (
    <div ref={setNodeRef} className="flex-1 min-w-[320px] flex flex-col">
      {/* Column Header - Gradient */}
      <div className={`flex items-center justify-between mb-4 px-5 py-4 rounded-2xl ${column.bgClass} border ${column.borderClass} dark:border-white/10 backdrop-blur-sm`}>
        <div className="flex items-center gap-3">
          <div className={`w-10 h-10 rounded-xl flex items-center justify-center ${
            column.color === 'orange' ? 'bg-gradient-to-br from-orange-500 to-red-500' :
            column.color === 'blue' ? 'bg-gradient-to-br from-blue-500 to-cyan-500' :
            'bg-gradient-to-br from-emerald-500 to-teal-500'
          } shadow-lg`}>
            <Icon className="w-5 h-5 text-white" strokeWidth={1.5} />
          </div>
          <div>
            <h3 className={`font-bold ${column.textClass}`} style={{ fontFamily: 'Outfit, sans-serif' }}>{column.title}</h3>
            <p className="text-xs text-muted-foreground">{documents.length} Dokumente</p>
          </div>
        </div>
        <div className="text-right">
          <p className={`text-2xl font-bold ${column.textClass}`}>{formatCurrency(totalAmount)}</p>
        </div>
      </div>

      {/* Column Content */}
      <div className={`flex-1 rounded-2xl p-4 min-h-[500px] bg-white/30 dark:bg-white/5 backdrop-blur-sm border border-dashed ${column.borderClass} dark:border-white/10`}>
        <SortableContext items={documents.map(d => d.id)} strategy={verticalListSortingStrategy}>
          <AnimatePresence>
            {documents.map((doc) => (
              <motion.div key={doc.id} initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0, y: -10 }}>
                <SortableCard doc={doc} onClick={() => onCardClick(doc)} />
              </motion.div>
            ))}
          </AnimatePresence>
        </SortableContext>
        {documents.length === 0 && (
          <div className="text-center py-12 text-muted-foreground">
            <div className={`w-16 h-16 mx-auto mb-4 rounded-2xl flex items-center justify-center ${column.bgClass}`}>
              <Icon className={`w-8 h-8 opacity-50 ${column.textClass}`} strokeWidth={1.5} />
            </div>
            <p className="font-medium">Keine Dokumente</p>
            <p className="text-sm">Hierher ziehen</p>
          </div>
        )}
      </div>
    </div>
  );
};

const BoardPage = () => {
  const navigate = useNavigate();
  const [documents, setDocuments] = useState([]);
  const [companies, setCompanies] = useState([]);
  const [selectedCompany, setSelectedCompany] = useState(null);
  const [loading, setLoading] = useState(true);
  const [uploading, setUploading] = useState(false);
  const [selectedDoc, setSelectedDoc] = useState(null);
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [activeId, setActiveId] = useState(null);

  const sensors = useSensors(
    useSensor(PointerSensor, { activationConstraint: { distance: 8 } }),
    useSensor(KeyboardSensor, { coordinateGetter: sortableKeyboardCoordinates })
  );

  const loadData = useCallback(async () => {
    try {
      const [docsRes, companiesRes] = await Promise.all([
        getDocuments(selectedCompany ? { company_id: selectedCompany.id } : {}),
        getCompanies()
      ]);
      setDocuments(docsRes.data);
      setCompanies(companiesRes.data);
    } catch (error) { toast.error('Fehler beim Laden'); }
    finally { setLoading(false); }
  }, [selectedCompany]);

  useEffect(() => { loadData(); }, [loadData]);

  const handleDragStart = (event) => setActiveId(event.active.id);

  const handleDragEnd = async (event) => {
    const { active, over } = event;
    setActiveId(null);
    if (!over) return;
    const activeDoc = documents.find(d => d.id === active.id);
    if (!activeDoc) return;
    let targetColumn = over.id;
    const overDoc = documents.find(d => d.id === over.id);
    if (overDoc) targetColumn = statusMapping[overDoc.status] || overDoc.status;
    const validColumns = COLUMNS.map(c => c.id);
    if (!validColumns.includes(targetColumn)) return;
    const currentMappedStatus = statusMapping[activeDoc.status] || activeDoc.status;
    if (currentMappedStatus === targetColumn) return;
    setDocuments(docs => docs.map(d => d.id === active.id ? { ...d, status: targetColumn } : d));
    try {
      await updateDocumentStatus(active.id, targetColumn);
      toast.success(targetColumn === 'done' ? 'Erledigt!' : targetColumn === 'progress' ? 'In Arbeit' : 'Offen');
    } catch (error) {
      setDocuments(docs => docs.map(d => d.id === active.id ? { ...d, status: activeDoc.status } : d));
      toast.error('Fehler');
    }
  };

  const handleFileUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setUploading(true);
    try { await uploadDocument(file, selectedCompany?.id, false); toast.success('Dokument hochgeladen!'); loadData(); }
    catch (error) { toast.error('Fehler beim Hochladen'); }
    finally { setUploading(false); }
  };

  const filteredDocuments = documents.filter(doc => {
    if (!searchQuery) return true;
    const query = searchQuery.toLowerCase();
    return doc.sender_name?.toLowerCase().includes(query) || doc.summary?.toLowerCase().includes(query) || doc.doc_type?.toLowerCase().includes(query);
  });

  const getDocsByColumn = (columnId) => filteredDocuments.filter(d => (statusMapping[d.status] || d.status) === columnId);
  const activeDoc = activeId ? documents.find(d => d.id === activeId) : null;

  if (loading) {
    return <div className="min-h-screen bg-background flex items-center justify-center"><div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div></div>;
  }

  return (
    <div className={`min-h-screen relative overflow-hidden ${document.documentElement.classList.contains('dark') ? 'galaxy-bg' : 'bg-background'}`} data-testid="board-page">
      {/* Light Mode Background Blobs */}
      <div className="absolute inset-0 overflow-hidden pointer-events-none dark:hidden">
        <div className="absolute -top-40 -right-40 w-80 h-80 bg-indigo-500/30 rounded-full mix-blend-multiply filter blur-3xl opacity-70 animate-blob"></div>
        <div className="absolute -bottom-40 -left-40 w-80 h-80 bg-violet-500/30 rounded-full mix-blend-multiply filter blur-3xl opacity-70 animate-blob animation-delay-2000"></div>
      </div>

      <div className="flex relative z-10">
        <Sidebar />
        <main className="flex-1 min-h-screen flex flex-col">
          <header className="glass-panel sticky top-0 z-40 border-b border-white/10 dark:bg-white/5 dark:border-white/10">
            <div className="px-6 py-4 flex items-center justify-between gap-4">
              <h1 className="text-xl font-semibold text-foreground" style={{ fontFamily: 'Outfit, sans-serif' }}>Board</h1>
              <div className="flex items-center gap-3 flex-1 max-w-xl">
                <div className="relative flex-1">
                  <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" strokeWidth={1.5} />
                  <Input value={searchQuery} onChange={(e) => setSearchQuery(e.target.value)} placeholder="Suchen..."
                    className="pl-10 h-10 rounded-xl glass-input dark:bg-white/10 dark:border-white/20" data-testid="board-search-input" />
                  {searchQuery && <button onClick={() => setSearchQuery('')} className="absolute right-3 top-1/2 -translate-y-1/2">
                    <X className="w-4 h-4 text-muted-foreground hover:text-foreground" />
                  </button>}
                </div>
                {companies.length > 0 && (
                  <DropdownMenu>
                    <DropdownMenuTrigger asChild>
                      <Button variant="outline" className="rounded-xl gap-2 glass-input dark:bg-white/10 dark:border-white/20">
                        <Building2 className="w-4 h-4" strokeWidth={1.5} />
                        <span className="hidden sm:inline max-w-[100px] truncate">{selectedCompany?.name || 'Alle'}</span>
                        <ChevronDown className="w-4 h-4" />
                      </Button>
                    </DropdownMenuTrigger>
                    <DropdownMenuContent className="glass-panel dark:bg-gray-900/90 dark:border-white/10">
                      <DropdownMenuItem onClick={() => setSelectedCompany(null)}>Alle Unternehmen</DropdownMenuItem>
                      {companies.map((c) => <DropdownMenuItem key={c.id} onClick={() => setSelectedCompany(c)}>{c.name}</DropdownMenuItem>)}
                    </DropdownMenuContent>
                  </DropdownMenu>
                )}
              </div>
              <label>
                <input type="file" accept=".pdf,image/*,.txt" className="hidden" onChange={handleFileUpload} disabled={uploading} />
                <Button className="bg-gradient-to-r from-violet-600 to-purple-600 hover:from-violet-700 hover:to-purple-700 text-white rounded-xl gap-2 shadow-lg shadow-violet-500/25" disabled={uploading} data-testid="board-upload-btn">
                  {uploading ? <RefreshCw className="w-4 h-4 animate-spin" /> : <Upload className="w-4 h-4" strokeWidth={1.5} />}
                  <span className="hidden sm:inline">Hochladen</span>
                </Button>
              </label>
            </div>
          </header>

          <div className="flex-1 overflow-x-auto p-6">
            <DndContext sensors={sensors} collisionDetection={closestCorners} onDragStart={handleDragStart} onDragEnd={handleDragEnd}>
              <div className="flex gap-4 min-h-[calc(100vh-200px)]">
                {COLUMNS.map((column) => <Column key={column.id} column={column} documents={getDocsByColumn(column.id)} onCardClick={(doc) => { setSelectedDoc(doc); setDrawerOpen(true); }} />)}
              </div>
              <DragOverlay>
                {activeDoc && (
                  <div className="glass-card px-4 py-3 rounded-xl shadow-xl border-2 border-violet-500 rotate-2 scale-105 dark:bg-white/10">
                    <div className="flex items-center justify-between gap-3">
                      <span className="font-medium text-foreground text-sm truncate">{activeDoc.sender_name || 'Unbekannt'}</span>
                      <span className="font-semibold text-foreground tabular-nums">{new Intl.NumberFormat('de-DE', { style: 'currency', currency: 'EUR' }).format(activeDoc.amount || 0)}</span>
                    </div>
                  </div>
                )}
              </DragOverlay>
            </DndContext>
          </div>
        </main>
      </div>
      <DocumentDrawer document={selectedDoc} open={drawerOpen} onClose={() => { setDrawerOpen(false); setSelectedDoc(null); }} onUpdate={loadData} />
    </div>
  );
};

export default BoardPage;
